<script lang="ts">
	import classNames from 'classnames';
	import dayjs from 'dayjs';
	import { twMerge } from 'tailwind-merge';
	import { ArtistCardTag } from '../NewsCard/ArtistCardTag';
	import { GeoCardTag } from '../NewsCard/GeoCardTag';
	import { HeniCardTag } from '../NewsCard/HeniCardTag';
	import { NewCategoryCardTag } from '../NewsCardGrid/NewCategoryCardTag';
	import type { NewsCardItem } from './types';
	import { Config } from '#lib/constants/config.js';
	import { SiteNames } from '#lib/constants/site-names.js';
	import { trackOnwardClick } from '#lib/features/analytics/utils/trackOnwardClick/trackOnwardClick.js';
	import { NewsMixpanelEvents } from '#lib/features/news/constants/mixpanel-events.js';
	import { getSortedCategories } from '#lib/features/news/utils/getSortedCategories/getSortedCategories.js';
	import { isHeniAuthoredUrl } from '#lib/features/news/utils/isHeniAuthoredUrl/isHeniAuthoredUrl.js';
	import { sanitizeArticleHref } from '#lib/features/news/utils/sanitizeArticleHref/sanitizeArticleHref.js';
	import { ArticleSearchCategory } from '#lib/websites/news/constants/categories.js';
	import { NewsRoutes } from '#lib/websites/news/constants/routes.js';
	import { page } from '$app/state';
	import { ShareIcon } from '$global/assets/icons/ShareIcon';
	import { createDialogStores } from '$global/components/Dialog';
	import { ShareDialog } from '$global/components/Share/ShareDialog';
	import { Skeleton } from '$global/components/Skeleton';
	import { Txt } from '$global/components/Txt';
	import { isMobileDevice } from '$global/utils/isMobileDevice';
	import { track } from '$global/utils/track';

	// Feed card widths: one column below sm, two up to lg, then a third of the
	// 1232px container. The CDN only serves WebP at 512 and 1024, so those are
	// the only renditions offered.
	const GRID_IMAGE_SIZES =
		'(min-width: 64rem) 26rem, (min-width: 40rem) 50vw, 100vw';

	let {
		clampable = false,
		item,
		onClick = undefined,
		classes = {},
		onclick,
		width = 1024,
		priority = false,
		sizes = GRID_IMAGE_SIZES,
		tagsHorizontalScrollMobile = true,
		...rest
	}: Props = $props();

	interface Props {
		/** Widest image rendition to request: 512 or 1024 (the CDN's WebP sizes). */
		width?: number;
		/** Load the image eagerly at high priority, for a card that is the LCP candidate. */
		priority?: boolean;
		/** `sizes` for the 512/1024 srcset; defaults to the feed grid's columns. */
		sizes?: string;
		class?: string;
		onclick?: () => void;
		clampable?: boolean | undefined;
		item: NewsCardItem | null;
		onClick?: (() => void) | undefined;
		classes?: { card?: string; info?: string; image?: string };
		tagsHorizontalScrollMobile?: boolean;
	}

	const dataCyPrefix = 'news-card';

	const imageAtWidth = (image: string, imageWidth: number) =>
		imageWidth === 1024
			? image
			: image.replace('width=1024', `width=${imageWidth}`);

	let showGlobalNav = $derived(page.data.showGlobalNav ?? true);
	let sortedCategories = $derived(getSortedCategories(item?.categories));

	// In app mode the News article route is swapped for its mobile equivalent.
	// The url is content-authored, so it is scheme-checked before it reaches the
	// anchor — see sanitizeArticleHref.
	let href = $derived(
		sanitizeArticleHref(
			showGlobalNav
				? item?.url
				: item?.url?.replace(NewsRoutes.Article, NewsRoutes.MobileArticle)
		)
	);

	const dialogStores = createDialogStores();

	const {
		states: { open },
	} = dialogStores;

	const handleOpenShareDialog = (e: MouseEvent) => {
		if (showGlobalNav) {
			open.set(true);
		} else {
			(window.location as unknown as string) = `/news?shareApp=1&text=${item?.title}&url=${Config.Domain}${NewsRoutes.View}/${item?.id}`;
		}

		e.preventDefault();
	};

	let handleClickPlatform = $derived((platform: string) => {
		track(NewsMixpanelEvents.ShareArticle, { id: item?.id, platform });
	});

	// `item.url` is free text on the feed item: usually another News article, but
	// it can point at a drop or any other section, which is the News→commerce
	// bridge this event exists to measure (HB-967). `trackOnwardClick` decides
	// from the resolved href, so the same-section majority costs nothing and
	// external syndicated urls are dropped on hostname.
	//
	// Fires alongside any `onclick` the parent passed, rather than replacing it.
	const handleClickCard = () => {
		trackOnwardClick({
			href,
			eventName: NewsMixpanelEvents.OnwardClick,
			sourceElement: 'news-card',
			artist: item?.artists
				?.map((entry) => entry?.HENI_News_Artists_id?.name)
				.filter(Boolean)
				.join(', '),
		});

		onclick?.();
	};

	let descriptionRef: HTMLParagraphElement | undefined = $state();

	let shareUrl = $derived(`${Config.Domain}${NewsRoutes.View}/${item?.id}`);
	let shareIntro = $derived(`HENI News: ${item?.title}`);
	let isHeniNewsItem = $derived(item?.attribution === SiteNames.HeniNews);

	// The card tag is driven by the article url: HENI-authored content on the
	// environment we are on, excluding the `/news/` path where aggregated
	// third-party press is republished. Originally url-based rather than the
	// gallery's `isHeni` flag (HB-948); the two now agree on the same rule.
	let isHeniArticle = $derived(isHeniAuthoredUrl(item?.url));
	let canBeClamped = $derived(
		clampable &&
			descriptionRef &&
			descriptionRef?.scrollHeight > descriptionRef?.clientHeight
	);

	// `clampable` is a static prop seeding the initial clamp state; the user toggles "read more" afterward.
	// svelte-ignore state_referenced_locally
	let clamped = $state(clampable);

	let date = $derived(
		dayjs(item?.publishDate || item?.date_updated || item?.date_created).format(
			'DD MMM, YYYY @ HH:mm'
		)
	);
</script>

{#if item}
	<a
		{href}
		target="_blank"
		rel="noopener noreferrer"
		class={rest.class}
		onclick={handleClickCard}
	>
		<div
			class={twMerge(
				'flex flex-col justify-between rounded border-b bg-white pb-6 sm:border',
				classes.card
			)}
		>
			<div>
				<Txt
					variant="h5"
					data-cy={`${dataCyPrefix}-title`}
					class={classNames('mt-0 mb-3 px-4 pt-8 sm:mt-6 sm:pt-0', {
						'line-clamp-3': clampable,
					})}
				>
					{item.title}
				</Txt>

				{#if item.image}
					<!-- Lazy by default: NewsCardGrid renders the feed once per breakpoint
					and hides the others, and only lazy images skip downloading inside a
					hidden layout. -->
					<img
						src={imageAtWidth(item.image, width)}
						srcset={width > 512
							? `${imageAtWidth(item.image, 512)} 512w, ${item.image} 1024w`
							: undefined}
						sizes={width > 512 ? sizes : undefined}
						{width}
						height={Math.round((width * 9) / 16)}
						loading={priority ? 'eager' : 'lazy'}
						fetchpriority={priority ? 'high' : undefined}
						decoding="async"
						alt={item.title}
						class={classNames(
							'aspect-[16/9] w-full object-cover px-4',
							classes.image
						)}
						data-cy={`${dataCyPrefix}-image`}
					/>
				{/if}

				<div class={classNames('', classes.info)}>
					<div class="relative">
						<!-- On mobile this row is a horizontal scroller. `overflow-x-auto`
						makes overflow-y compute as auto too, so the row becomes the pills'
						clip box — and with no bottom padding the pills sat flush against
						that edge, where iOS Safari cut off their bottom border. The
						spacing below the pills is therefore padding inside the scrollport
						(`pb-3`) rather than margin outside it, and `horizontal-scroll-hint`
						stops the vertical axis from scrolling at all. -->
						<div
							class={classNames('no-scrollbar mr-4 flex gap-2 px-4 pt-3', {
								'flex-wrap': !(tagsHorizontalScrollMobile && isMobileDevice()),
								'horizontal-scroll-hint overflow-x-auto whitespace-nowrap':
									tagsHorizontalScrollMobile && isMobileDevice(),
								'mb-1 pb-3':
									isHeniArticle ||
									sortedCategories.includes(ArticleSearchCategory.shoutouts) ||
									item.geotag?.name ||
									item.artists?.length,
							})}
						>
							{#if typeof window !== 'undefined'}
								{#if isHeniArticle}
									<HeniCardTag showNewVersion dataCy={dataCyPrefix} {onClick} />
								{/if}
								{#if sortedCategories.includes(ArticleSearchCategory.shoutouts)}
									<NewCategoryCardTag
										dataCy={dataCyPrefix}
										category={ArticleSearchCategory.shoutouts}
										{onClick}
									/>
								{/if}
								{#if item.geotag?.name}
									<GeoCardTag
										showNewVersion
										dataCy={dataCyPrefix}
										name={item.geotag?.name}
										{onClick}
									/>
								{/if}
								{#if item.artists}
									{#each item.artists as artist}
										{#if artist?.HENI_News_Artists_id?.name}
											<ArtistCardTag
												showNewVersion
												dataCy={dataCyPrefix}
												name={artist?.HENI_News_Artists_id?.name}
												{onClick}
											/>
										{/if}
									{/each}
								{/if}
							{/if}
						</div>
						<div
							class="pointer-events-none absolute top-0 right-0 h-full w-12 bg-gradient-to-l from-white to-transparent"
						></div>
					</div>

					<div class="max-w-[100vw] px-4">
						<div class={classNames('relative mb-3', { 'h-[60px]': clamped })}>
							<Txt
								variant="body2"
								component="div"
								class={classNames({ 'line-clamp-3': clamped })}
							>
								<p bind:this={descriptionRef}>
									{item.description}
								</p>
							</Txt>
							{#if canBeClamped && clamped}
								<div class="bg-gray-0 absolute right-0 bottom-[-1px]">
									<Txt variant="body2" component="span">...</Txt>
									<button
										type="button"
										onclick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											clamped = false;
										}}
									>
										<Txt variant="body2" component="span" class="font-[600]"
											>Show More</Txt
										>
									</button>
								</div>
							{/if}
						</div>
					</div>
				</div>
			</div>

			<div class="flex items-center justify-between gap-4 px-4">
				<Txt
					variant="body3"
					class="mb-2 text-[10px] leading-[160%] font-[500] sm:mb-0"
				>
					{#if item.attribution}
						<span
							class={isHeniNewsItem
								? 'text-[12px] tracking-widest uppercase'
								: 'text-[10px]'}
						>
							{item.attribution}
						</span>

						<span class="text-[10px] text-gray-500">&middot;</span>
					{/if}

					<span class="text-[10px] text-gray-500">
						{date}
					</span>
				</Txt>

				<button
					class="border-none"
					onclick={handleOpenShareDialog}
					aria-label="Share article"
					data-cy={`${dataCyPrefix}-share-button`}
				>
					<ShareIcon class="h-5 w-5" />
				</button>
			</div>
		</div>
	</a>

	<ShareDialog
		colored
		{dialogStores}
		link={shareUrl}
		intro={shareIntro}
		dataCy={dataCyPrefix}
		showTwitter={false}
		onClickPlatform={handleClickPlatform}
	/>
{:else}
	<div>
		<div class="border-b border-gray-200 bg-white px-4 py-6 sm:border">
			<Skeleton class="mb-3 h-[1.25rem] w-full" />
			<Skeleton
				class="mb-3 aspect-[3/2] w-full object-cover"
				variant="rectangular"
			/>
			<Skeleton class="mb-2 h-[1.75rem] w-full" />
			<Skeleton class="mb-3 h-[1.25rem] w-full" />
			<Skeleton class="h-[1.25rem] w-1/2" />
		</div>
	</div>
{/if}
